/**
 * AllGroupsTab — server-side searched and sorted directory of every group,
 * with the join CTA (mobile allGroupsTab parity).
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGroup } from '@wlb/shared/context/GroupContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import useDebounce from '@wlb/shared/hooks/useDebounce';
import type { Group, GroupSortOption } from '@wlb/shared/types/groups';
import { ListStatus } from '../../components/ui/ListStatus';
import { GroupRow } from './GroupRow';
import { GroupRowsSkeleton } from './GroupSkeletons';
import { GroupSearchBar } from './GroupSearchBar';
import { SortGroupsModal } from './SortGroupsModal';
import { useScrollRestore } from './useScrollRestore';
import '../../components/ui/skeleton.css';
import '../feed/feed.css';
import './groups.css';

export function AllGroupsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    allGroups,
    allGroupsLoading,
    allGroupsError,
    allGroupsLoaded,
    fetchAllGroups,
    joinGroup,
  } = useGroup();

  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState<GroupSortOption>('newest');
  const [sortOpen, setSortOpen] = useState(false);
  const debouncedQuery = useDebounce(searchQuery.trim(), 350);

  const load = useCallback(() => {
    fetchAllGroups({ sortBy: sortOption, searchTerm: debouncedQuery || undefined }).catch(
      () => undefined,
    );
  }, [fetchAllGroups, sortOption, debouncedQuery]);

  useEffect(load, [load]);

  /** Member-initiated reload — bypasses the cache key so it always hits the API. */
  const refresh = useCallback(() => {
    fetchAllGroups(
      { sortBy: sortOption, searchTerm: debouncedQuery || undefined },
      { force: true },
    ).catch(() => undefined);
  }, [fetchAllGroups, sortOption, debouncedQuery]);

  // Reset scroll to top whenever the query or sort changes so a stale offset
  // from a previous result set is not restored into a shorter list.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [sortOption, debouncedQuery]);

  useScrollRestore('groups:allGroups', !allGroupsLoading || allGroups.length > 0);

  const handleSelect = (group: Group) => navigate(`/groups/${group.id}`);
  const handleJoin = (groupId: string) => { joinGroup(groupId).catch(() => undefined); };

  const renderList = () => {
    if (allGroupsLoading && allGroups.length === 0) {
      return <GroupRowsSkeleton />;
    }
    if (allGroupsError && allGroups.length === 0) {
      return (
        <ListStatus
          variant="error"
          message={allGroupsError}
          onAction={refresh}
          busy={allGroupsLoading}
        />
      );
    }
    if (!allGroupsLoaded && allGroups.length === 0) {
      return <GroupRowsSkeleton />;
    }
    if (allGroups.length === 0) {
      // A search that matched nothing is not a stale directory, so refreshing
      // is only offered when the member is looking at the unfiltered list.
      return (
        <ListStatus
          variant="empty"
          title={t('home.allGroups.emptyList')}
          onAction={debouncedQuery ? undefined : refresh}
          busy={allGroupsLoading}
        />
      );
    }
    return (
      <div className="all-groups__list skeleton-fade-in">
        {allGroups.map(group => (
          <GroupRow key={group.id} group={group} onSelect={handleSelect} onJoin={handleJoin} />
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="my-groups__header">
        <GroupSearchBar
          inputId="all-groups-search"
          value={searchQuery}
          onChange={setSearchQuery}
          onFilter={() => setSortOpen(true)}
        />
      </div>
      {renderList()}
      {sortOpen && (
        <SortGroupsModal
          selected={sortOption}
          onClose={() => setSortOpen(false)}
          onApply={setSortOption}
        />
      )}
    </>
  );
}
